import { Box, Divider, Grid } from '@mui/material';
import { GridSortModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ColumnSettings } from 'wisestep-api';
import {
  getDateUsingFormat,
  ScrollableTabs,
  SearchBarNew,
  WsBadge,
  WsBadgeSize,
  WsBadgetype,
  WsButton,
  WsColors,
  WsColorsNew,
  WsDataGrid,
  WsDatePicker,
  WsDrawer,
  WsPaginationBar,
  WsSelect,
  WsTypography,
} from 'wisestep-base';

import Spinner from '../../../components/Spinner';
import { Page } from '../../../types/Page';
import { defaultEmptyPage } from '../../../utils/defaultPageData';
import Resizable from '../../candidateLayout/components/childComponets/resizable/Resizable';
import { EmailPreviewCard } from '../../emailSettings/components/emailPreviewCard';
import { NoEmailCampaignResponse } from '../../emailSettings/components/noEmailResponse';
import SmartFilter from '../../submissionsNew/components/filters/smart/SmartFilter';
import { useTableColumnSettings } from '../hooks/useTableColumnSettings';
import {
  CallDetails,
  CommType,
  EmailDetails,
  ICommunicationLog,
  IFilterState,
  SmsDetails,
  TaggedPageType,
} from '../types/CommunicationTypes';
import {
  commTabs,
  communicationTypeOptions,
  defaultDateFilter,
  defaultSortModel,
  getAllCommunicationLogs,
  getCommTableColumns,
  getTableKeysFor,
  taggedPageFieldMap,
} from '../utils/communicationUtils';

import CallTranscript from './CallTranscript';
import EmailViewer from './EmailViewer';
import MessageHistory from './MessageHistory';

const AllCommunicationsLog = ({ entityId, type }: { entityId: string; type: TaggedPageType }) => {
  const [activeTab, setActiveTab] = useState<CommType>('All');
  const [loading, setLoading] = useState<boolean>(false);
  const [communicationLogs, setCommunicationLogs] =
    useState<Page<ICommunicationLog>>(defaultEmptyPage);
  const [activeLog, setActiveLog] = useState<ICommunicationLog | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>(defaultSortModel);
  const [pageNum, setPageNum] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const tableKey = getTableKeysFor(type)[activeTab];
  const { columnOptions, handleColumnSettingsChange } = useTableColumnSettings(
    tableKey as ColumnSettings
  );

  const formMethods = useForm<IFilterState>({
    defaultValues: {
      ...defaultDateFilter(),
      communicationType: activeTab,
      searchText: '',
      userType: null,
    },
  });
  const { getValues, setValue, reset, handleSubmit } = formMethods;

  const handleTabChange = (tabId: number) => setActiveTab(commTabs[tabId].tabName);

  const handleDrawerClose = () => setActiveLog(null);

  const fetchCommunicationLogs = useCallback(
    async (payload: IFilterState = getValues(), signal?: AbortSignal) => {
      setLoading(true);
      try {
        const reqBody = {
          [taggedPageFieldMap[type]]: entityId,
          page: pageNum,
          size: pageSize,
          sortField: sortModel[0]?.field,
          sortDirection: sortModel[0]?.sort,
          greaterThan: payload.fromDate,
          lessThan: payload.toDate,
          direction: payload.direction,
          type: activeTab === 'All' ? payload.communicationType : activeTab,
          searchText: payload.searchText,
        };
        const response = await getAllCommunicationLogs(reqBody, { signal });
        setCommunicationLogs(response.data);
        setActiveLog(null);
      } finally {
        !signal?.aborted && setLoading(false);
      }
    },
    [activeTab, entityId, getValues, pageNum, pageSize, sortModel, type]
  );

  useEffect(() => {
    if (['SMS', 'Email'].includes(activeTab) == false) {
      setValue('direction', null);
    }
    setSortModel(defaultSortModel);
    setActiveLog(null);
    setPageNum(0);
  }, [activeTab, setValue]);

  useEffect(() => {
    const controller = new AbortController();
    fetchCommunicationLogs(undefined, controller.signal);

    return () => controller.abort();
  }, [fetchCommunicationLogs]);

  const columns = useMemo(() => getCommTableColumns(activeTab, setActiveLog), [activeTab]);

  const handleClearDateFilter = () => {
    const defaultDate = defaultDateFilter();
    setValue('fromDate', defaultDate.fromDate);
    setValue('toDate', defaultDate.toDate);
    fetchCommunicationLogs();
  };

  const handleClearCommFilter = () => {
    setValue('communicationType', null);
    fetchCommunicationLogs();
  };

  const handleClearDirectionFilter = () => {
    setValue('direction', null);
    fetchCommunicationLogs();
  };

  const handleClearAllFilters = () => {
    reset({
      searchText: getValues('searchText'),
      communicationType: activeTab,
      ...defaultDateFilter(),
    });
    fetchCommunicationLogs();
  };

  const fetchCommunicationLogsWrapper = () => fetchCommunicationLogs();
  const isDateFilterActive = () => Boolean(getValues('fromDate')) || Boolean(getValues('toDate'));

  return (
    <Grid container rowGap={2} p={1}>
      <Grid item xs={12}>
        <ScrollableTabs scrollList={commTabs} getTabIndex={handleTabChange} />
      </Grid>
      <FormProvider {...formMethods}>
        <Grid container item xs={12} spacing={2} component="form" onSubmit={handleSubmit(() => {})}>
          <Grid item xs={4}>
            <SearchBarNew name="searchText" onSearch={fetchCommunicationLogsWrapper} />
          </Grid>
          {activeTab === 'All' && (
            <Grid item xs="auto">
              <SmartFilter
                filterName="Communication"
                onClick={fetchCommunicationLogsWrapper}
                onClear={handleClearCommFilter}
                filterValue={getValues('communicationType')}
              >
                <WsSelect
                  placeholder="Select Communication Type"
                  name="communicationType"
                  selectArray={communicationTypeOptions}
                />
              </SmartFilter>
            </Grid>
          )}
          {['SMS', 'Email'].includes(activeTab) && (
            <Grid item xs="auto">
              <SmartFilter
                filterName="Message Direction"
                onClick={fetchCommunicationLogsWrapper}
                onClear={handleClearDirectionFilter}
                filterValue={getValues('direction')}
              >
                <WsSelect
                  name="direction"
                  placeholder="Select Direction"
                  selectArray={[
                    { label: 'Inbound', value: 'INBOUND' },
                    { label: 'Outbound', value: 'OUTBOUND' },
                  ]}
                />
              </SmartFilter>
            </Grid>
          )}
          <Grid item xs="auto">
            <SmartFilter
              filterName="Date Range"
              onClick={fetchCommunicationLogsWrapper}
              onClear={handleClearDateFilter}
              filterValue={isDateFilterActive()}
            >
              <WsDatePicker name="fromDate" label="From Date" disableFuture={true} />
              <WsDatePicker name="toDate" label="To Date" disableFuture={true} />
            </SmartFilter>
          </Grid>
        </Grid>
      </FormProvider>
      <Grid container item xs={12} spacing={2}>
        {activeTab === 'All' &&
          getValues('communicationType') &&
          getValues('communicationType') !== 'All' && (
            <Grid item xs="auto">
              <WsBadge
                label={`Communication: ${getValues('communicationType')}`}
                badgeType={WsBadgetype.INACTIVE}
                endIcon={true}
                onClick={handleClearCommFilter}
                badgeSize={WsBadgeSize.LARGE}
              />
            </Grid>
          )}
        {['SMS', 'Email'].includes(activeTab) && getValues('direction') && (
          <Grid item xs="auto">
            <WsBadge
              label={`Direction: ${getValues('direction')}`}
              badgeType={WsBadgetype.INACTIVE}
              endIcon={true}
              onClick={handleClearDirectionFilter}
              badgeSize={WsBadgeSize.LARGE}
            />
          </Grid>
        )}
        {isDateFilterActive() && (
          <Grid item xs="auto">
            <WsBadge
              label={`Date Range: ${new Date(
                getValues('fromDate')
              )?.toLocaleDateString()} - ${new Date(getValues('toDate'))?.toLocaleDateString()}`}
              badgeType={WsBadgetype.INACTIVE}
              endIcon={true}
              onClick={handleClearDateFilter}
              badgeSize={WsBadgeSize.LARGE}
            />
          </Grid>
        )}
        <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center' }}>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              backgroundColor: WsColorsNew.neutral_400a,
              height: 30,
              mx: 0.5,
            }}
          />
          <WsButton onClick={handleClearAllFilters} variant="text">
            Clear Filters
          </WsButton>
        </Grid>
      </Grid>
      {activeTab !== 'Email' && (
        <Grid item xs={12} position="relative">
          {loading && <Spinner posY={15} backdrop />}
          <WsDataGrid
            autoHeight
            rows={communicationLogs.content}
            columns={columns}
            getRowId={(row) => row.sourceId}
            selectionModel={activeLog?.sourceId ?? ''}
            paginationMode="server"
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            totalRows={communicationLogs.totalElements}
            page={pageNum}
            onPageChange={setPageNum}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            columnVisibility
            columnSettings={columnOptions}
            onColumnSettingsChange={handleColumnSettingsChange}
          />
        </Grid>
      )}
      {activeTab === 'Email' && (
        <Grid item xs={12} position="relative">
          {loading && <Spinner posY={12} backdrop />}
          <WsPaginationBar
            totalRecords={communicationLogs.totalElements}
            page={pageNum}
            pageSize={pageSize}
            onPageChange={setPageNum}
            onPageSizeChange={setPageSize}
          />
          <Divider />
          <Resizable
            leftPanel={
              communicationLogs.numberOfElements > 0 ? (
                <Box>
                  {communicationLogs.content.map((emailLog) => (
                    <Box
                      key={emailLog.sourceId}
                      onClick={() => setActiveLog(emailLog)}
                      sx={{
                        bgcolor:
                          activeLog?.sourceId === emailLog.sourceId
                            ? WsColors.BackGroundBrandLight
                            : '',
                      }}
                    >
                      <EmailPreviewCard
                        userName={emailLog.contactFrom ?? 'Not Available'}
                        subject={(emailLog.details as EmailDetails)?.subject ?? 'Not Available'}
                        emailSentTime={
                          emailLog.createdOn
                            ? getDateUsingFormat(emailLog.createdOn.toString(), 'DD MMM')
                            : ''
                        }
                        bodyPreview={(emailLog.details as EmailDetails)?.body ?? 'Not Available'}
                        jobInfo={`${emailLog.jobTitle}`}
                      />
                      <Divider />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box mt={3} textAlign="center">
                  <WsTypography>
                    No messages matched your search. Try using search options such as direction,
                    date, size and more.
                  </WsTypography>
                </Box>
              )
            }
            rightPanel={
              activeLog ? (
                <EmailViewer emailLog={activeLog as ICommunicationLog<EmailDetails>} />
              ) : (
                <NoEmailCampaignResponse
                  text="No Email Logs"
                  subText="Click on any email to view details"
                />
              )
            }
            minSize="25%"
            maxSize="50%"
            defaultSizes={['30%', 'auto']}
          />
        </Grid>
      )}
      <WsDrawer
        size="compact"
        open={activeLog?.type === 'Call'}
        onClose={handleDrawerClose}
        title="Call Transcript"
      >
        <CallTranscript callLog={activeLog as ICommunicationLog<CallDetails>} />
      </WsDrawer>
      <WsDrawer
        size="medium"
        open={activeLog?.type === 'Email' && activeTab === 'All'}
        onClose={handleDrawerClose}
        title="Email Transcript"
      >
        <EmailViewer emailLog={activeLog as ICommunicationLog<EmailDetails>} />
      </WsDrawer>
      <WsDrawer
        size="compact"
        open={activeLog?.type === 'SMS'}
        onClose={handleDrawerClose}
        title="SMS Transcript"
      >
        <MessageHistory smsLog={activeLog as ICommunicationLog<SmsDetails>} />
      </WsDrawer>
    </Grid>
  );
};

export default AllCommunicationsLog;
